import Decimal from 'decimal.js';
import { DateTime } from 'luxon';


class formulaEvaluator {
    constructor(data = {}) {
        this.data = data;

        // Map operators to JS functions
        this.lazyFunctions = new Set(['LET', 'IF', 'SWITCH', 'WHILE', 'FOR', 'MAP', 'REDUCE']);


        this.operatorFns = {
            '+': (a, b) => {
                if (typeof a === 'string' || typeof b === 'string') return String(a) + String(b);
                return new Decimal(a).plus(new Decimal(b)).toNumber();
            },
            '-': (a, b) => new Decimal(a).minus(new Decimal(b)).toNumber(),
            '*': (a, b) => new Decimal(a).times(new Decimal(b)).toNumber(),
            '/': (a, b) => new Decimal(a).div(new Decimal(b)).toNumber(),
            '=': (a, b) => a === b,
            '==': (a, b) => a === b,
            '!=': (a, b) => a !== b,
            '<>': (a, b) => a !== b,
            '<': (a, b) => new Decimal(a).lt(new Decimal(b)),
            '<=': (a, b) => new Decimal(a).lte(new Decimal(b)),
            '>': (a, b) => new Decimal(a).gt(new Decimal(b)),
            '>=': (a, b) => new Decimal(a).gte(new Decimal(b)),
            AND: (a, b) => Boolean(a) && Boolean(b),
            OR: (a, b) => Boolean(a) || Boolean(b),
            NOT: (a) => !Boolean(a),
        };

        this.functions = {
            LEN: (arg) => (typeof arg === 'string' ? [...arg].length : String(arg).length),
            UPPER: (arg) => String(arg).toUpperCase(),
            LOWER: (arg) => String(arg).toLowerCase(),
            CONCAT: (...args) => args.map(String).join(''),
            CONCATENATE: (...args) => args.map(String).join(''),
            TRIM: (arg) => String(arg).trim(),
            LEFT: (str, count) => String(str).substring(0, count),
            RIGHT: (str, count) => String(str).slice(-count),
            MID: (str, start, length) => String(str).substr(start, length),
            SUBSTITUTE: (str, find, replacement) => String(str).split(find).join(replacement),
            REPLACE: (str, start, length, replacement) => {
                str = String(str);
                return str.slice(0, start) + replacement + str.slice(start + length);
            },
            FIND: (substr, str) => String(str).indexOf(substr),
            SEARCH: (substr, str) => String(str).toLowerCase().indexOf(substr.toLowerCase()),
            IF: (cond, trueVal, falseVal) => (cond ? trueVal : falseVal),

            LET: (name, valueExprTokens, exprTokens, context) => {
                const value = this.evaluateTokens(valueExprTokens, context);
                const newContext = { ...context, [name]: value };
                // console.log("newContext", newContext)
                return this.evaluateTokens(exprTokens, newContext);
            },



            FOR: (varName, startTokens, endTokens, exprTokens, context) => {
                // Evaluate start and end tokens first to get actual numbers
                const start = this.evalTokens(startTokens, context);
                const end = this.evalTokens(endTokens, context);

                // console.log("FOR", varName, start, end, exprTokens);

                const results = [];
                for (let i = start; i <= end; i++) {
                    // Extend context with current loop variable value
                    const iterContext = { ...context, [varName]: i };

                    // console.log("iterContext", varName, iterContext);

                    // Evaluate expression tokens with new context
                    const value = this.evalTokens(exprTokens, iterContext);

                    results.push(value);
                }

                return results;
            },

            WHILE: (condTokens, bodyTokens, context) => {
                let result;
                let maxIterations = 10000;  // safety limit to prevent infinite loops
                let count = 0;

                while (true) {
                    const cond = this.evalTokens(condTokens, context);
                    if (!cond) break;
                    result = this.evalTokens(bodyTokens, context);
                    if (++count >= maxIterations) {
                        throw new Error('WHILE loop exceeded maximum iterations');
                    }
                }

                return result;
            },

            MAP: (arr, exprTokens, context, iterVar = 'item') => {
                if (!Array.isArray(arr)) return [];
                return arr.map(item => {
                    const iterContext = { ...context, [iterVar]: item };
                    return this.evalTokens(exprTokens, iterContext);
                });
            },

            REDUCE: (arr, exprTokens, initial, context, accVar = 'acc', itemVar = 'item') => {
                if (!Array.isArray(arr)) return initial;

                let acc = initial;

                for (const item of arr) {
                    const iterContext = { ...context, [accVar]: acc, [itemVar]: item };
                    acc = this.evalTokens(exprTokens, iterContext);
                }

                return acc;
            },



            AND: (...args) => args.every(Boolean),
            OR: (...args) => args.some(Boolean),
            NOT: (arg) => !arg,
            XOR: (a, b) => Boolean(a) !== Boolean(b),
            TRUE: () => true,
            FALSE: () => false,
            BLANK: () => null,
            ERROR: () => null,
            ISBLANK: (arg) => arg == null,
            ISNULL: (arg) => arg == null,
            ISERROR: (arg) => {
                try {
                    return arg === null || arg === undefined;
                } catch {
                    return true;
                }
            },
            SWITCH: (expr, ...cases) => {
                for (let i = 0; i < cases.length - 1; i += 2) {
                    if (expr === cases[i]) return cases[i + 1];
                }
                return cases.length % 2 === 1 ? cases[cases.length - 1] : null;
            },

            ABS: (arg) => {
                const val = Number(arg);
                if (isNaN(val)) return 0;
                return new Decimal(val).abs().toNumber();
            },
            CEILING: (arg) => new Decimal(arg).ceil().toNumber(),
            FLOOR: (arg) => new Decimal(arg).floor().toNumber(),
            ROUND: (num, places = 0) => new Decimal(num).toDecimalPlaces(places).toNumber(),
            ROUNDDOWN: (arg) => new Decimal(arg).floor().toNumber(),
            ROUNDUP: (arg) => new Decimal(arg).ceil().toNumber(),
            SQRT: (arg) => new Decimal(arg).sqrt().toNumber(),
            POWER: (base, exp) => new Decimal(base).pow(exp).toNumber(),
            LOG: (arg) => Math.log(new Decimal(arg).toNumber()),
            LOG10: (arg) => Math.log10(new Decimal(arg).toNumber()),
            EXP: (arg) => Math.exp(new Decimal(arg).toNumber()),
            MOD: (a, b) => new Decimal(a).mod(new Decimal(b)).toNumber(),
            MIN: (...args) => Math.min(...args.map(a => new Decimal(a).toNumber())),
            MAX: (...args) => Math.max(...args.map(a => new Decimal(a).toNumber())),
            SUM: (...args) => args.reduce((acc, cur) => new Decimal(acc).plus(cur), new Decimal(0)).toNumber(),
            AVERAGE: (...args) => {
                const total = args.reduce((acc, cur) => new Decimal(acc).plus(cur), new Decimal(0));
                return total.dividedBy(args.length).toNumber();
            },
            // ABS: (arg) => Math.abs(arg),
            // CEILING: (arg) => Math.ceil(arg),
            // FLOOR: (arg) => Math.floor(arg),
            // ROUND: (num, places = 0) => Number(num.toFixed(places)),
            // ROUNDDOWN: (arg) => Math.floor(arg),
            // ROUNDUP: (arg) => Math.ceil(arg),
            // SQRT: (arg) => Math.sqrt(arg),
            // POWER: (base, exp) => Math.pow(base, exp),
            POW: (base, exp) => Math.pow(base, exp),
            // LOG: (arg) => Math.log(arg),
            // LOG10: (arg) => Math.log10(arg),
            // EXP: (arg) => Math.exp(arg),
            // MOD: (a, b) => a % b,
            EVEN: (num) => (num % 2 === 0 ? num : num + 1),
            ODD: (num) => (num % 2 !== 0 ? num : num + 1),
            // MIN: (...args) => Math.min(...args),
            // MAX: (...args) => Math.max(...args),
            TRUNC: (arg) => Math.trunc(arg),
            // INT: (arg) => Math.trunc(arg),
            INT: (arg) => Math.trunc(Number(arg)),

            // SUM: (...args) => args.reduce((a, b) => a + b, 0),
            COUNT: (arr) => (Array.isArray(arr) ? arr.length : 0),
            COUNTA: (arr) => (Array.isArray(arr) ? arr.filter(x => x != null).length : 0),
            COUNTALL: (arr) => (Array.isArray(arr) ? arr.length : 0),
            // AVERAGE: (...args) => args.reduce((a, b) => a + b, 0) / args.length,
            SIN: (arg) => Math.sin(arg),
            COS: (arg) => Math.cos(arg),
            TAN: (arg) => Math.tan(arg),
            ASIN: (arg) => Math.asin(arg),
            ACOS: (arg) => Math.acos(arg),
            ATAN: (arg) => Math.atan(arg),
            SIGN: (arg) => Math.sign(arg),
            RADIANS: (deg) => (deg * Math.PI) / 180,
            DEGREES: (rad) => (rad * 180) / Math.PI,



            // Returns current date and time (Date object)
            // NOW: (timezone = 'local') => {
            //     const dt = timezone && timezone !== 'local'
            //         ? DateTime.now().setZone(timezone)
            //         : DateTime.now();
            //     return dt.toISO();
            // },
            // Returns current date with zeroed time (midnight)
            // TODAY: () => {
            //     const now = new Date();
            //     return new Date(now.getFullYear(), now.getMonth(), now.getDate());
            // },

            // Constructs a date from year, month (1-based), day
            DATE: (year, month, day) => new Date(year, month - 1, day),

            // Constructs a time from hours, minutes, seconds (returns Date with today’s date)
            TIME: (hours = 0, minutes = 0, seconds = 0) => {
                const d = new Date();
                d.setHours(hours, minutes, seconds, 0);
                return d;
            },

            // Extract year from date
            // YEAR: (date) => (date instanceof Date ? date.getFullYear() : null),

            // // Extract month (1-based) from date
            // MONTH: (date) => (date instanceof Date ? date.getMonth() + 1 : null),

            // // Extract day of month from date
            // DAY: (date) => (date instanceof Date ? date.getDate() : null),

            // // Extract hour from date/time
            // HOUR: (date) => (date instanceof Date ? date.getHours() : null),

            // Extract minute from date/time
            // MINUTE: (date) => (date instanceof Date ? date.getMinutes() : null),

            // Extract second from date/time
            // SECOND: (date) => (date instanceof Date ? date.getSeconds() : null),

            // Return day of week (1=Sunday, 7=Saturday) or optionally ISO (Monday=1)
            WEEKDAY: (date, type = 1) => {
                if (!(date instanceof Date)) return null;
                const day = date.getDay(); // 0=Sunday,6=Saturday
                if (type === 1) return day + 1; // Sunday=1...Saturday=7
                if (type === 2) return day === 0 ? 7 : day; // Monday=1...Sunday=7 (ISO)
                return day + 1; // default Sunday=1
            },




            NOW: (timezone = 'local') => {
                if (timezone && typeof timezone != 'string') {
                    timezone = 'local'
                }
                const dt = timezone !== 'local' ? DateTime.now().setZone(timezone) : DateTime.now();

                if (!dt.isValid) {
                    console.error("Invalid timezone:", timezone);
                    return null;
                }
                return dt.toISO();
            },

            TODAY: (timezone = 'local') => {
                const dt = timezone !== 'local' ? DateTime.now().setZone(timezone) : DateTime.now();
                return dt.startOf('day').toISODate(); // returns "YYYY-MM-DD"
            },

            DATEADD: (dateInput, amount, unit, inputFormat = null, timezone = 'local') => {
                if (dateInput) {
                    const units = {
                        year: 'years', month: 'months', week: 'weeks', day: 'days',
                        hour: 'hours', minute: 'minutes', second: 'seconds', millisecond: 'milliseconds',
                        years: 'years', months: 'months', weeks: 'weeks', days: 'days',
                        hours: 'hours', minutes: 'minutes', seconds: 'seconds', milliseconds: 'milliseconds',
                    };

                    const u = units[String(unit).toLowerCase()];
                    const amt = Number(amount);
                    if (isNaN(amt) || !u) throw new Error(`Invalid unit or amount in DATEADD`);
                    let dt = /^\d+$/.test(dateInput)
                        ? DateTime.fromMillis(Number(dateInput))
                        : inputFormat
                            ? DateTime.fromFormat(dateInput, inputFormat)
                            : DateTime.fromISO(dateInput);

                    if (timezone !== 'local') dt = dt.setZone(timezone);
                    if (!dt.isValid) return null;

                    // return dt.plus({ [u]: amt }).toFormat(inputFormat);
                    return dt.plus({ [u]: amt }).toISO();
                } else {
                    return ''
                }
            },

            DATEDIFF: (d1, d2, unit, timezone = 'local') => {
                const units = {
                    year: 'years', month: 'months', week: 'weeks', day: 'days',
                    hour: 'hours', minute: 'minutes', second: 'seconds',
                    years: 'years', months: 'months', weeks: 'weeks', days: 'days',
                    hours: 'hours', minutes: 'minutes', seconds: 'seconds'
                };
                const u = units[String(unit).toLowerCase()];
                if (!u) throw new Error(`Invalid unit in DATEDIFF`);

                let dt1 = /^\d+$/.test(d1) ? DateTime.fromMillis(Number(d1)) : DateTime.fromISO(d1);
                let dt2 = /^\d+$/.test(d2) ? DateTime.fromMillis(Number(d2)) : DateTime.fromISO(d2);

                // console.log("dt1",dt1,dt2)
                if (timezone !== 'local') {
                    dt1 = dt1.setZone(timezone);
                    dt2 = dt2.setZone(timezone);
                }

                if (!dt1.isValid || !dt2.isValid) return null;

                return Math.floor(dt1.diff(dt2, u).toObject()[u]);
            },

            FORMATDATE: (dateInput, outputFormat = "yyyy-MM-dd HH:mm", inputFormat = null, timezone = 'local') => {
                let dt = /^\d+$/.test(dateInput)
                    ? DateTime.fromMillis(Number(dateInput))
                    : inputFormat
                        ? DateTime.fromFormat(dateInput, inputFormat)
                        : DateTime.fromISO(dateInput);

                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.toFormat(outputFormat) : null;
            },

            YEAR: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.year : null;
            },

            MONTH: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.month : null;
            },

            DAY: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.day : null;
            },

            HOUR: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.hour : null;
            },

            MINUTE: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.minute : null;
            },

            SECOND: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.second : null;
            },

            DAYOFWEEK: (d, timezone = 'local') => {
                let dt = /^\d+$/.test(d) ? DateTime.fromMillis(Number(d)) : DateTime.fromISO(d);
                if (timezone !== 'local') dt = dt.setZone(timezone);
                return dt.isValid ? dt.weekday : null; // 1 = Monday, 7 = Sunday
            },

            ISBEFORE: (d1, d2, timezone = 'local') => {
                let dt1 = /^\d+$/.test(d1) ? DateTime.fromMillis(Number(d1)) : DateTime.fromISO(d1);
                let dt2 = /^\d+$/.test(d2) ? DateTime.fromMillis(Number(d2)) : DateTime.fromISO(d2);
                if (timezone !== 'local') {
                    dt1 = dt1.setZone(timezone);
                    dt2 = dt2.setZone(timezone);
                }
                return dt1.isValid && dt2.isValid ? dt1 < dt2 : null;
            },

            ISAFTER: (d1, d2, timezone = 'local') => {
                let dt1 = /^\d+$/.test(d1) ? DateTime.fromMillis(Number(d1)) : DateTime.fromISO(d1);
                let dt2 = /^\d+$/.test(d2) ? DateTime.fromMillis(Number(d2)) : DateTime.fromISO(d2);
                if (timezone !== 'local') {
                    dt1 = dt1.setZone(timezone);
                    dt2 = dt2.setZone(timezone);
                }
                return dt1.isValid && dt2.isValid ? dt1 > dt2 : null;
            },
            // Format date to string using simple pattern (limited, e.g. "YYYY-MM-DD")
            // (You can enhance with date-fns or similar libs if you want)
            // DATEFORMAT: (date, format) => {
            //     if (!(date instanceof Date)) return null;
            //     const pad = (n) => (n < 10 ? '0' + n : n);
            //     return format
            //         .replace(/YYYY/g, date.getFullYear())
            //         .replace(/MM/g, pad(date.getMonth() + 1))
            //         .replace(/DD/g, pad(date.getDate()))
            //         .replace(/HH/g, pad(date.getHours()))
            //         .replace(/mm/g, pad(date.getMinutes()))
            //         .replace(/ss/g, pad(date.getSeconds()));
            // },

            // Return week number of year (ISO 8601)
            WEEKNUM: (date) => {
                if (!(date instanceof Date)) return null;
                const target = new Date(date.valueOf());
                const dayNr = (date.getDay() + 6) % 7;
                target.setDate(target.getDate() - dayNr + 3);
                const firstThursday = target.valueOf();
                target.setMonth(0, 1);
                if (target.getDay() !== 4) {
                    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
                }
                return 1 + Math.ceil((firstThursday - target) / (7 * 24 * 60 * 60 * 1000));
            },

            // Return quarter of the year (1 to 4)
            QUARTER: (date) => {
                if (!(date instanceof Date)) return null;
                return Math.floor(date.getMonth() / 3) + 1;
            },

            // Returns TRUE if date is a weekend (Saturday or Sunday)
            ISWEEKEND: (date) => {
                if (!(date instanceof Date)) return null;
                const day = date.getDay();
                return day === 0 || day === 6;
            },

            // Returns TRUE if date is a weekday (Monday-Friday)
            ISWEEKDAY: (date) => {
                if (!(date instanceof Date)) return null;
                const day = date.getDay();
                return day >= 1 && day <= 5;
            },

            // Returns difference in hours between two dates
            HOURSDIFF: (startDate, endDate) => {
                if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
                const diffMs = endDate - startDate;
                return diffMs / (1000 * 60 * 60);
            },

            // Returns difference in minutes between two dates
            MINUTESDIFF: (startDate, endDate) => {
                if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
                const diffMs = endDate - startDate;
                return diffMs / (1000 * 60);
            },

            // Returns difference in seconds between two dates
            SECONDSDIFF: (startDate, endDate) => {
                if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
                const diffMs = endDate - startDate;
                return diffMs / 1000;
            },
        };
    }







    parseValueOrField(token, context) {
        if (typeof token === 'string') {
            token = token.trim();

            // Match [user.name] or [name]
            let bracketMatch = token.match(/^\[([^\]]+)\]$/);
            if (!bracketMatch) {
                // Match {{user.name}} or {{name}}
                bracketMatch = token.match(/^\{\{([^}]+)\}\}$/);
            }

            if (bracketMatch) {
                const path = bracketMatch[1].trim();
                return this.getNestedValue(this.data, path);
            }

            // Number literal
            if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);

            // String literal with quotes
            if (/^"(.*)"$/.test(token)) return token.slice(1, -1);

            // Raw variable like name, user.name
            if (/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(token)) {
                // First try context
                if (token in context) return context[token];
                return this.getNestedValue(this.data, token);
            }
        }

        if (typeof token === 'object' && token.type === 'string') {
            return token.value;
        }

        return token;
    }




    evaluateTokens(tokens, context) {
        return this.evalTokens(tokens, context);
    }

    getNestedValue(obj, path) {
        if (!obj || !path) return undefined;
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
            if (current && Object.prototype.hasOwnProperty.call(current, part)) {
                current = current[part];
            } else {
                return undefined;
            }
        }
        return current;
    }


    isNumber(value) {
        return typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value);
    }

    // evaluate(formula, data) {

    //     this.data = data
    //     try {
    //         const tokens = this.tokenize(formula);
    //         return this.evalTokens(tokens);
    //     } catch (e) {
    //         return `Error: ${e.message}`;
    //     }
    // }




    safeTokenize(input) {
        if (typeof input !== 'string') {
            input = String(input);
        }
        return this.tokenize(input);
    }


    processArg = (argTokens) => {
        console.log("processArg", argTokens)
        // If the argument is a single token that's an object with type 'string', extract the value
        if (argTokens.length === 1 && typeof argTokens[0] === 'object' && argTokens[0].type === 'string') {
            return argTokens[0].value;
        }
        // Otherwise, evaluate the tokens as normal
        return argTokens
    };


    // evaluate(formula, data = {}) {
    //     try {

    //         this.data = data;
    //         const tokens = this.safeTokenize(formula);
    //         // console.log("tokens", tokens)
    //         return this.evalTokens(tokens);

    //     } catch (e) {
    //         console.log(e)
    //     }
    // }


    toPostfix(tokens) {
        const output = [];
        const stack = [];
        const precedence = { '+': 1, '-': 1, '*': 2, '/': 2 };
        const rightAssociative = new Set([]);

        for (const token of tokens) {
            if (!isNaN(token)) {
                output.push(token);
            } else if (token in precedence) {
                while (
                    stack.length &&
                    (precedence[stack[stack.length - 1]] > precedence[token] ||
                        (precedence[stack[stack.length - 1]] === precedence[token] && !rightAssociative.has(token)))
                ) {
                    output.push(stack.pop());
                }
                stack.push(token);
            } else if (token === '(') {
                stack.push(token);
            } else if (token === ')') {
                while (stack.length && stack[stack.length - 1] !== '(') {
                    output.push(stack.pop());
                }
                stack.pop(); // Remove the '('
            }
        }

        while (stack.length) {
            output.push(stack.pop());
        }

        return output;
    }


    evalPostfix(postfix) {
        const stack = [];
        for (const token of postfix) {
            if (!isNaN(token)) {
                stack.push(parseFloat(token));
            } else if (['+', '-', '*', '/'].includes(token)) {
                const b = stack.pop();
                const a = stack.pop();
                stack.push(this.operatorFns[token](a, b));
            } else {
                throw new Error(`Unknown token: ${token}`);
            }
        }
        return stack[0];
    }



    evaluate(formula, data = {}) {
        try {
            this.data = data;
            const tokens = this.safeTokenize(formula);
            const postfix = this.toPostfix(tokens);
            return this.evalPostfix(postfix);
        } catch (e) {
            console.error("Evaluation error:", e);
            return null;
        }
    }






    tokenize(formula) {

        if (typeof formula !== 'string') {
            throw new Error(`Invalid input to tokenize: expected string, got ${typeof formula},${formula}`);
        }

        const tokens = [];
        let i = 0;

        const isDigit = (char) => /[0-9.]/.test(char);

        while (i < formula.length) {
            const c = formula[i];

            // Skip spaces
            if (/\s/.test(c)) {
                i++;
                continue;
            }

            // Handle string literals
            if (c === '"') {
                i++;
                let str = '';
                while (i < formula.length && formula[i] !== '"') {
                    str += formula[i++];
                }
                i++; // Skip closing "
                tokens.push({ type: 'string', value: str });
                continue;
            }

            // Handle {{var}}
            if (i + 1 < formula.length && formula[i] === '{' && formula[i + 1] === '{') {
                i += 2;
                let varName = '';
                while (i + 1 < formula.length && !(formula[i] === '}' && formula[i + 1] === '}')) {
                    varName += formula[i++];
                }
                i += 2; // skip }}
                tokens.push(varName.trim());
                continue;
            }

            // Handle [var]
            if (formula[i] === '[') {
                i++;
                let varName = '';
                while (i < formula.length && formula[i] !== ']') {
                    varName += formula[i++];
                }
                i++; // skip ]
                tokens.push(varName.trim());
                continue;
            }

            // Handle negative number (unary)
            if (
                c === '-' &&
                (
                    tokens.length === 0 ||
                    ['(', ',', '+', '-', '*', '/', '=', '<', '>', '!'].includes(tokens[tokens.length - 1])
                ) &&
                i + 1 < formula.length && isDigit(formula[i + 1])
            ) {
                let num = '-';
                i++;
                while (i < formula.length && isDigit(formula[i])) {
                    num += formula[i++];
                }
                tokens.push(num);
                continue;
            }

            // Handle numbers
            if (isDigit(c)) {
                let num = '';
                while (i < formula.length && isDigit(formula[i])) {
                    num += formula[i++];
                }
                tokens.push(num);
                continue;
            }

            // Handle multi-character operators
            const twoChar = formula.slice(i, i + 2);
            const multiOps = ['>=', '<=', '!=', '==', '<>', '&&', '||'];
            if (multiOps.includes(twoChar)) {
                tokens.push(twoChar === '&&' ? 'AND' : twoChar === '||' ? 'OR' : twoChar);
                i += 2;
                continue;
            }

            // Handle single-character operators and punctuation
            if ('()+-*/<>=!&|,'.includes(c)) {
                tokens.push(c);
                i++;
                continue;
            }

            // Handle identifiers (functions/variables)
            let id = '';
            while (i < formula.length && /[a-zA-Z0-9_.]/.test(formula[i])) {
                id += formula[i++];
            }
            if (id) {
                tokens.push(id);
                continue;
            }

            // Fallback: unknown character, just skip
            i++;
        }

        return tokens;
    }



    // tokenize(formula) {
    //     const regex = /"(?:[^"\\]|\\.)*"|\{\{[^}]+\}\}|\[[^\]]+\]|[a-zA-Z_][\w.]*|\d+\.\d+|\d+|==|!=|<=|>=|<>|[+\-*/=<>(),]/g;

    //     const tokens = [];
    //     let match;

    //     while ((match = regex.exec(formula)) !== null) {
    //         const token = match[0];

    //         // Remove surrounding quotes from string literals
    //         if (/^"(.*)"$/.test(token)) {
    //             tokens.push({ type: 'string', value: token.slice(1, -1) });
    //         } else {
    //             tokens.push(token);
    //         }
    //     }

    //     return tokens;
    // }



    // tokenize(formula) {
    //     const tokens = [];
    //     let buffer = '';
    //     let i = 0;

    //     const flushBuffer = () => {
    //         if (buffer.trim() !== '') tokens.push(buffer.trim());
    //         buffer = '';
    //     };

    //     while (i < formula.length) {
    //         const c = formula[i];

    //         if (c === '"') {
    //             flushBuffer();
    //             i++;
    //             let str = '';
    //             while (i < formula.length && formula[i] !== '"') {
    //                 str += formula[i++];
    //             }
    //             i++;
    //             tokens.push(str);
    //             continue;
    //         }

    //         if (/[\s]/.test(c)) {
    //             flushBuffer();
    //             i++;
    //             continue;
    //         }

    //         if ('()+-*/<>=!&|,'.includes(c)) {
    //             flushBuffer();

    //             // Handle multi-char operators
    //             let op = c;

    //             if (
    //                 (c === '<' || c === '>' || c === '=' || c === '!') &&
    //                 i + 1 < formula.length &&
    //                 formula[i + 1] === '='
    //             ) {
    //                 op += '=';
    //                 i++;
    //             } else if (c === '<' && i + 1 < formula.length && formula[i + 1] === '>') {
    //                 op += '>';
    //                 i++;
    //             } else if (c === '&' && i + 1 < formula.length && formula[i + 1] === '&') {
    //                 op = 'AND';
    //                 i++;
    //             } else if (c === '|' && i + 1 < formula.length && formula[i + 1] === '|') {
    //                 op = 'OR';
    //                 i++;
    //             } else if (c === '-' && (
    //                 tokens.length === 0 ||
    //                 tokens[tokens.length - 1] === '(' ||
    //                 tokens[tokens.length - 1] === ',' ||
    //                 this.operatorFns[tokens[tokens.length - 1]]
    //             )) {
    //                 // Handle unary negative number like -5.5
    //                 let num = '-';
    //                 i++;
    //                 while (i < formula.length && /[\d.]/.test(formula[i])) {
    //                     num += formula[i++];
    //                 }
    //                 tokens.push(num);
    //                 continue;
    //             }

    //             tokens.push(op);
    //             i++;
    //             continue;
    //         }

    //         buffer += c;
    //         i++;
    //     }
    //     flushBuffer();
    //     return tokens;
    // }


    processArgs = (args) => {
        return args.map(arg => {
            if (Array.isArray(arg)) {
                // Recursively process array elements and return immediately
                return this.processArgs(arg);
            }
            if (typeof arg === 'object' && arg !== null && arg.type === 'string' && 'value' in arg) {
                // Unwrap the { type: 'string', value } object to just value
                return arg.value;
            }
            // Otherwise, return as-is
            return arg;
        });
    }


    evalTokens(tokens, context) {
        // console.log("evalTokens", tokens, context)
        const isFunction = (name) => typeof this.functions[name.toUpperCase()] === 'function';

        // Constants that do NOT require parentheses
        const constants = new Set(['TRUE', 'FALSE', 'NULL', 'BLANK', 'ERROR']);

        const evalExpr = (i, context) => {
            let token = tokens[i];

            // console.log('Token', token)
            // Handle string literal
            if (typeof token === 'object' && token.type === 'string') {
                return [token.value, i + 1];
            }

            if (typeof token === 'string') {
                const upperToken = token.toUpperCase();

                // Handle constants
                if (constants.has(upperToken)) {
                    switch (upperToken) {
                        case 'TRUE': return [true, i + 1];
                        case 'FALSE': return [false, i + 1];
                        case 'NULL':
                        case 'BLANK':
                        case 'ERROR': return [null, i + 1];
                    }
                }

                // Handle functions
                if (isFunction(token)) {
                    if (tokens[i + 1] !== '(') throw new Error(`Expected '(' after function ${token}`);

                    let depth = 1;
                    let j = i + 2;
                    let args = [];
                    let currentArg = [];

                    while (j < tokens.length) {
                        if (tokens[j] === '(') depth++;
                        else if (tokens[j] === ')') depth--;

                        if (depth === 0) break;

                        if (tokens[j] === ',' && depth === 1) {
                            if (this.lazyFunctions.has(upperToken)) {
                                args.push(currentArg); // pass raw tokens
                            } else {
                                args.push(this.evalTokens(currentArg, context));  // default: eager evaluation
                            }
                            currentArg = []
                        } else {
                            currentArg.push(tokens[j]);
                        }
                        j++;
                    }
                    // console.log("currentArg",currentArg)
                    if (currentArg.length > 0) {
                        if (this.lazyFunctions.has(upperToken)) {
                            args.push(currentArg); // pass raw tokens
                        } else {
                            args.push(this.evalTokens(currentArg, context));  // default: eager evaluation
                        }
                    }
                    // console.log("upperToken",  args)

                    const processedArgs = this.processArgs(args)

                    // console.log("processedArgs", processedArgs)
                    const result = this.functions[upperToken](...processedArgs, context);
                    return [result, j + 1];

                }

                // Handle number literal (support negative and decimals)
                if (/^-?\d+(\.\d+)?$/.test(token)) {
                    return [parseFloat(token), i + 1];
                }

                // Handle variable: [name], {{name}}, [user.name], etc.
                let resolvedKey = null;

                const bracketMatch = token.match(/^\[([^\]]+)\]$/);
                const curlyMatch = token.match(/^\{\{([^}]+)\}\}$/);
                if (bracketMatch) {
                    resolvedKey = bracketMatch[1];
                } else if (curlyMatch) {
                    resolvedKey = curlyMatch[1];
                }

                if (resolvedKey) {
                    const value = resolvedKey.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), context);
                    if (value == null) {
                        value = resolvedKey.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), this.data);
                    }
                    return [value !== undefined ? value : '', i + 1];
                }

                // Fallback: plain variable name (e.g., name)
                const fallbackValue = context?.[token] ?? this.data?.[token];
                return [fallbackValue !== undefined ? fallbackValue : '', i + 1];

            }

            return [token, i + 1];
        };

        let i = 0;
        let [left, next] = evalExpr(i, context);
        i = next;

        while (i < tokens.length) {
            const op = tokens[i];
            const [right, nextIndex] = evalExpr(i + 1, context);
            if (!this.operatorFns[op]) {
                // console.log(this.data,tokens)
                console.error(`Unknown operator: ${op}`);
                return
            }
            left = this.operatorFns[op](left, right);
            i = nextIndex;
        }

        return left;
    }


}

export default new formulaEvaluator();